import React, { useState, useEffect } from 'react';
import { FaFile, FaFileImage, FaFilePdf, FaFileWord, FaFileExcel, FaFilePowerpoint, FaFileCode, FaFileArchive, FaFileAudio, FaFileVideo, FaDownload, FaEye, FaTimes } from 'react-icons/fa';
import ImageViewer from './ImageViewer';
import './AttachmentPreview.css';

const FILE_TYPE_ICONS = {
  'image': FaFileImage,
  'pdf': FaFilePdf,
  'text': FaFileCode,
  'log': FaFileCode,
  'default': FaFile
};

const FILE_TYPE_NAMES = {
  'image': 'Image',
  'pdf': 'PDF Document',
  'text': 'Text File',
  'log': 'Log File',
  'default': 'File'
};

function getFileType(fileUrl) {
  if (!fileUrl) return 'default';
  
  const url = fileUrl.toLowerCase();
  if (url.includes('.jpg') || url.includes('.jpeg') || url.includes('.png') || url.includes('.gif') || url.includes('.bmp') || url.includes('.webp')) {
    return 'image';
  } else if (url.includes('.pdf')) {
    return 'pdf';
  } else if (url.includes('.txt') || url.includes('.md')) {
    return 'text';
  } else if (url.includes('.log')) {
    return 'log';
  }
  return 'default';
}

function getFileName(fileUrl) {
  if (!fileUrl) return 'Unknown File';
  const parts = fileUrl.split('/');
  return parts[parts.length - 1] || 'Unknown File';
}

export default function AttachmentPreview({ attachment, onClose }) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [previewError, setPreviewError] = useState(false);
  
  if (!attachment) return null;
  
  const fileType = getFileType(attachment);
  const fileName = getFileName(attachment);
  const IconComponent = FILE_TYPE_ICONS[fileType];
  const fileTypeName = FILE_TYPE_NAMES[fileType];
  
  const handlePreview = () => {
    if (fileType === 'image') {
      setIsPreviewOpen(true);
      setPreviewError(false);
    } else if (fileType === 'pdf') {
      // Open PDF in new tab
      window.open(attachment, '_blank');
    } else if (fileType === 'text' || fileType === 'log') {
      // For text files, try to fetch and display content
      setIsPreviewOpen(true);
      setPreviewError(false);
    } else {
      // For other file types, just download
      const link = document.createElement('a');
      link.href = attachment;
      link.download = fileName;
      link.click();
    }
  };
  
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = attachment;
    link.download = fileName;
    link.click();
  };
  
  const renderPreview = () => {
    if (fileType === 'image') {
      return (
        <div className="preview-content">
          <img 
            src={attachment} 
            alt={fileName}
            onError={(e) => {
              console.error('Image load error:', e);
              console.error('Failed to load image:', attachment);
              setPreviewError(true);
            }}
            onLoad={() => {
              console.log('Image loaded successfully:', attachment);
              setPreviewError(false);
            }}
            crossOrigin="anonymous"
            style={{ maxWidth: '100%', maxHeight: '70vh', objectFit: 'contain' }}
          />
          {previewError && (
            <div className="preview-error">
              <p>Failed to load image preview</p>
              <p style={{ fontSize: '0.8em', color: '#666', marginTop: '8px' }}>
                This might be due to CORS restrictions. Try downloading the file instead.
              </p>
              <button onClick={handleDownload} className="download-fallback-btn">
                <FaDownload /> Download Instead
              </button>
            </div>
          )}
        </div>
      );
    } else if (fileType === 'text' || fileType === 'log') {
      return (
        <div className="preview-content">
          <TextFilePreview fileUrl={attachment} fileName={fileName} />
        </div>
      );
    }
    return null;
  };
  
  return (
    <>
      <div className="attachment-preview">
        <div className="attachment-info">
          <div className="attachment-icon">
            <IconComponent />
          </div>
          <div className="attachment-details">
            <div className="attachment-name">{fileName}</div>
            <div className="attachment-type">{fileTypeName}</div>
          </div>
        </div>
        
        <div className="attachment-actions">
          <button 
            className="preview-btn"
            onClick={handlePreview}
            title={fileType === 'image' ? 'Preview Image' : fileType === 'pdf' ? 'Open PDF' : 'Preview File'}
          >
            <FaEye />
          </button>
          <button 
            className="download-btn"
            onClick={handleDownload}
            title="Download File"
          >
            <FaDownload />
          </button>
        </div>
      </div>
      
      {/* Preview Modal */}
      {isPreviewOpen && (
        <div className="preview-modal-overlay" onClick={() => setIsPreviewOpen(false)}>
          <div className="preview-modal" onClick={(e) => e.stopPropagation()}>
            <div className="preview-modal-header">
              <h3>{fileName}</h3>
              <button 
                className="close-preview-btn"
                onClick={() => setIsPreviewOpen(false)}
              >
                <FaTimes />
              </button>
            </div>
            {renderPreview()}
            <div className="preview-modal-footer">
              <button onClick={handleDownload} className="download-btn">
                <FaDownload /> Download
              </button>
              <button onClick={() => setIsPreviewOpen(false)} className="close-btn">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

// Component for previewing text files
function TextFilePreview({ fileUrl, fileName }) {
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  useEffect(() => {
    const fetchTextContent = async () => {
      try {
        setLoading(true);
        setError(false);
        
        const response = await fetch(fileUrl);
        if (!response.ok) {
          throw new Error('Failed to fetch file content');
        }
        
        const text = await response.text();
        setContent(text);
      } catch (err) {
        console.error('Error fetching text file:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    
    fetchTextContent();
  }, [fileUrl]);
  
  if (loading) {
    return (
      <div className="text-preview-loading">
        <div className="loading-spinner"></div>
        <p>Loading file content...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="text-preview-error">
        <p>Failed to load file content</p>
        <p>This might be due to CORS restrictions or the file not being accessible.</p>
      </div>
    );
  }
  
  return (
    <div className="text-preview-content">
      <pre className="text-content">{content}</pre>
    </div>
  );
}
